/**
 * Chooses a focused set of columns for the Stores page table (summary / identity / location).
 * Full flattened column order from the API is unchanged for Excel export.
 */

import {humanizeColumnKey} from 'utils/humanizeColumnKey';

export type StoresTableDisplayColumn = {
  id: string;
  label: string;
};

/** Ordered UI columns: match field → optional header label (default: matched column id). */
const PREFERRED_DISPLAY: Array<{matchField: string; label?: string}> = [
  {matchField: 'Geo'},
  {matchField: 'Country', label: 'Country'},
  {matchField: 'SiteId'},
  {matchField: 'StoreNumber'},
  {matchField: 'StoreName'},
  {matchField: 'Brand'},
  {matchField: 'Region'},
  {matchField: 'StoreConcept'},
  {matchField: 'Email'},
  {matchField: 'PhoneNumber'},
  {matchField: 'Address1', label: 'Address'},
  {matchField: 'City', label: 'City'},
  {matchField: 'State', label: 'State'},
  {matchField: 'PostalCode', label: 'Postal code'},
  {matchField: 'Latitude'},
  {matchField: 'Longitude'},
  {matchField: 'TimeZone - Zone Short', label: 'Timezone code'},
];

function fieldMatchesColumn(field: string, column: string): boolean {
  const f = field.toLowerCase().replace(/\s+/g, '');
  const c = column.toLowerCase().replace(/\s+/g, '');
  if (c === f) {
    return true;
  }
  const parts = column.split(' - ').map(s => s.trim());
  const last = parts[parts.length - 1];
  if (last && last.toLowerCase().replace(/\s+/g, '') === f) {
    return true;
  }
  return column.toLowerCase().endsWith(' - ' + field.toLowerCase());
}

/** Resolves the flattened column key for Country in the current API payload (for filtering). */
export function findStoresCountryColumnId(allColumns: string[]): string | undefined {
  return allColumns.find(col => fieldMatchesColumn('Country', col));
}

/**
 * Returns which columns to show in the UI table, in a stable summary-first order.
 */
export function pickStoresTableDisplayColumns(allColumns: string[]): StoresTableDisplayColumn[] {
  if (allColumns.length === 0) {
    return [];
  }

  const used = new Set<string>();
  const ordered: StoresTableDisplayColumn[] = [];

  for (const {matchField, label} of PREFERRED_DISPLAY) {
    const match = allColumns.find(
      col => !used.has(col) && fieldMatchesColumn(matchField, col)
    );
    if (match) {
      ordered.push({id: match, label: label ?? humanizeColumnKey(match)});
      used.add(match);
    }
  }

  if (ordered.length > 0) {
    return ordered;
  }

  const byDepth = [...allColumns].sort((a, b) => {
    const depth = (s: string) => (s.match(/ - /g) || []).length;
    const d = depth(a) - depth(b);
    if (d !== 0) {
      return d;
    }
    return a.localeCompare(b);
  });
  return byDepth.slice(0, 12).map(id => ({id, label: humanizeColumnKey(id)}));
}
